import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type {
  WizardState,
  WizardStep,
  Subject,
  Paper,
  Chapter,
  ExamType,
  Department,
  QuestionVersion,
  DifficultyLevel,
} from '../types/wizard';
import { WIZARD_STEPS, requiresDepartment } from '../types/wizard';
import type { GeneratedExamPaper } from '../types/generated';

interface WizardContextValue {
  state: WizardState;
  currentStep: WizardStep;
  currentStepIndex: number;
  totalSteps: number;
  goNext: () => void;
  goPrev: () => void;
  goToStep: (step: WizardStep) => void;
  setClass: (classNum: number) => void;
  setVersion: (version: QuestionVersion) => void;
  setDepartment: (department: Department) => void;
  setSubject: (subject: Subject) => void;
  setPaper: (paper: Paper) => void;
  toggleChapter: (chapter: Chapter) => void;
  setExamType: (examType: ExamType) => void;
  setInstitutionName: (name: string) => void;
  setSubjectCode: (code: string) => void;
  setNumberOfQuestions: (n: number) => void;
  setDuration: (minutes: number) => void;
  setTotalMarks: (marks: number) => void;
  setDifficulty: (level: DifficultyLevel) => void;
  canGoNext: boolean;
  onComplete?: (paper: GeneratedExamPaper) => void;
}

const WizardContext = createContext<WizardContextValue | null>(null);

const INITIAL_STATE: WizardState = {
  selectedClass: null,
  selectedVersion: null,
  selectedDepartment: null,
  selectedSubject: null,
  selectedPaper: null,
  selectedChapters: [],
  selectedExamType: null,
  institutionName: '',
  subjectCode: '',
  numberOfQuestions: 10,
  duration: 180,
  totalMarks: 100,
  difficulty: 'mixed',
};

function isStepComplete(step: WizardStep, state: WizardState): boolean {
  switch (step) {
    case 'class':
      return state.selectedClass !== null;
    case 'version':
      return state.selectedVersion !== null;
    case 'department':
      // auto-complete for classes 1–8 (no department needed)
      return !requiresDepartment(state.selectedClass) || state.selectedDepartment !== null;
    case 'subject':
      return state.selectedSubject !== null;
    case 'paper':
      return state.selectedPaper !== null;
    case 'chapter':
      return state.selectedChapters.length > 0;
    case 'examType':
      return state.selectedExamType !== null;
    case 'questions':
      return state.numberOfQuestions > 0;
    case 'review':
      return true;
    case 'generate':
      return true;
    default:
      return false;
  }
}

export function WizardProvider({
  children,
  onComplete,
}: Readonly<{ children: React.ReactNode; onComplete?: (paper: GeneratedExamPaper) => void }>) {
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = WIZARD_STEPS[currentStepIndex];
  const totalSteps = WIZARD_STEPS.length;
  const canGoNext = isStepComplete(currentStep, state);

  const goNext = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((i) => i + 1);
    }
  }, [currentStepIndex, totalSteps]);

  const goPrev = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((i) => i - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((step: WizardStep) => {
    const idx = WIZARD_STEPS.indexOf(step);
    if (idx !== -1) setCurrentStepIndex(idx);
  }, []);

  const setClass = useCallback((classNum: number) => {
    setState((s) => ({
      ...s,
      selectedClass: classNum,
      selectedVersion: null,
      selectedDepartment: null,
      selectedSubject: null,
      selectedPaper: null,
      selectedChapters: [],
      selectedExamType: null,
    }));
  }, []);

  const setVersion = useCallback((version: QuestionVersion) => {
    setState((s) => ({ ...s, selectedVersion: version }));
  }, []);

  const setDepartment = useCallback((department: Department) => {
    setState((s) => ({
      ...s,
      selectedDepartment: department,
      selectedSubject: null,
      selectedPaper: null,
      selectedChapters: [],
      selectedExamType: null,
    }));
  }, []);

  const setSubject = useCallback((subject: Subject) => {
    setState((s) => ({
      ...s,
      selectedSubject: subject,
      selectedPaper: null,
      selectedChapters: [],
      selectedExamType: null,
    }));
  }, []);

  const setPaper = useCallback((paper: Paper) => {
    setState((s) => ({
      ...s,
      selectedPaper: paper,
      selectedChapters: [],
      selectedExamType: null,
    }));
  }, []);

  const toggleChapter = useCallback((chapter: Chapter) => {
    setState((s) => {
      const exists = s.selectedChapters.some((c) => c.id === chapter.id);
      return {
        ...s,
        selectedChapters: exists
          ? s.selectedChapters.filter((c) => c.id !== chapter.id)
          : [...s.selectedChapters, chapter],
      };
    });
  }, []);

  const setExamType = useCallback((examType: ExamType) => {
    setState((s) => ({ ...s, selectedExamType: examType }));
  }, []);

  const setInstitutionName = useCallback((name: string) => {
    setState((s) => ({ ...s, institutionName: name }));
  }, []);

  const setSubjectCode = useCallback((code: string) => {
    setState((s) => ({ ...s, subjectCode: code }));
  }, []);

  const setNumberOfQuestions = useCallback((n: number) => {
    setState((s) => ({ ...s, numberOfQuestions: Math.max(1, n) }));
  }, []);

  const setDuration = useCallback((minutes: number) => {
    setState((s) => ({ ...s, duration: Math.max(15, minutes) }));
  }, []);

  const setTotalMarks = useCallback((marks: number) => {
    setState((s) => ({ ...s, totalMarks: Math.max(1, marks) }));
  }, []);

  const setDifficulty = useCallback((level: DifficultyLevel) => {
    setState((s) => ({ ...s, difficulty: level }));
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      currentStep,
      currentStepIndex,
      totalSteps,
      goNext,
      goPrev,
      goToStep,
      setClass,
      setVersion,
      setDepartment,
      setSubject,
      setPaper,
      toggleChapter,
      setExamType,
      setInstitutionName,
      setSubjectCode,
      setNumberOfQuestions,
      setDuration,
      setTotalMarks,
      setDifficulty,
      canGoNext,
      onComplete,
    }),
    [state, currentStep, currentStepIndex, canGoNext, onComplete]
  );

  return <WizardContext.Provider value={contextValue}>{children}</WizardContext.Provider>;
}

export function useWizard(): WizardContextValue {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used inside <WizardProvider>');
  return ctx;
}
